// Libraries
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { rem } from 'polished';

// Components
import GracefulImage from '../../../../ui/layout/graceful-image/index';
import TeamAvatar from '../../../../ui/layout/team-avatar/index';

const roundToOne = num => {
	return +(Math.round(num + 'e+1') + 'e-1');
};

/**********
 ** Component: TeamSideInfo
 ** @type: functional stateless component
 ** @description: Aside info of the user when it is logged in
 **********/
const TeamSideInfo = props => {
	const renderUserSidePicture = (photoUrl, nick, float) => {
		if (photoUrl) {
			return (
				<GracefulImage
					src={photoUrl}
					width="138"
					height="138"
					alt={`This is: ${nick}`}
					placeholderColor="hsl(240, 9%, 98%)"
					onError={event => {
						event.target.src =
							'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGRhdGEtbmFtZT0iTGF5ZXIgMSIgdmlld0JveD0iMCAwIDEzOCAxMzgiPjxwYXRoIGZpbGw9IiNlYmVjZWYiIGQ9Ik0wIDBoMTM4djEzOEgweiIvPjxjaXJjbGUgY3g9IjY3Ljc5IiBjeT0iNTAuOCIgcj0iMjQiIGZpbGw9IiNkMWQyZGIiLz48cGF0aCBmaWxsPSIjZDFkMmRiIiBkPSJNMTkuNzkgMTE1LjY3YzAtMTYuNTYgMzItMjUuNjcgNDgtMjUuNjdzNDggOS4xMSA0OCAyNS42N1YxMzhoLTk2eiIvPjwvc3ZnPg==';
					}}
					noLazyLoad={true}
				/>
			);
		} else {
			if (float) {
				return (
					<TeamAvatar
						large
						nick={nick}
						style={{
							marginBottom: 0,
						}}
					/>
				);
			}
			return <TeamAvatar large nick={nick} />;
		}
	};

	return (
		<Wrapper className={`cell ${props.className}`} float={props.float}>
			<figure className="profile__aside__avatar">
				{renderUserSidePicture(props.photoUrl, props.nick, props.float)}
			</figure>
			<div className="profile__aside__meta">
				<h2 className="profile__aside__distance">{`${roundToOne(props.distance)}Km`}</h2>
				<span className="profile__aside__position">{`#${props.position}`}</span>
			</div>
		</Wrapper>
	);
};

const Wrapper = styled.aside`
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: center;
	padding: 0 ${props => props.theme.globalMargin};

	margin-top: 0;
	margin-bottom: ${rem('32px')};

	@media ${props => props.theme.breakpointLarge} {
		margin-top: ${props => (props.float ? `0` : `${rem('-96px')}`)};
		margin-bottom: 0;
	}

	.profile {
		&__aside {
			&__avatar {
				width: ${rem('96px')};
				height: ${rem('96px')};
				border-radius: ${rem('96px')};
				display: flex;
				margin-bottom: ${rem('16px')};
				display: flex;
				flex-direction: row;
				justify-content: center;
				align-items: center;

				img {
					width: 100%;
					height: 100%;
					border-radius: ${rem('96px')};
					object-fit: cover;
					object-position: center center;
					margin: 0;
					padding: 0;
					background-color: ${props => props.theme.primaryColor};
					color: ${props => props.theme.white};
					font-size: ${rem('60px')};
					font-weight: 600;
					letter-spacing: 1.25px;
					text-align: center;
					display: flex;
					flex-direction: row;
					justify-content: center;
					align-items: center;
				}

				@media ${props => props.theme.breakpointLarge} {
					width: ${rem('138px')};
					height: ${rem('138px')};
					border-radius: ${rem('138px')};
					img {
						border-radius: ${rem('138px')};
					}
				}
			}
			&__meta {
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: flex-start;
			}

			&__meta,
			&__distance,
			&__position {
				text-align: center;
				width: 100%;
				color: ${props => props.theme.gray9};
				letter-spacing: 0;
				line-height: 1;
			}

			&__distance {
				font-weight: 600;
				font-size: ${rem('40px')};
			}

			&__position {
				padding: ${rem('4px')} ${rem('8px')};
				line-height: 1;
				border: 1px solid ${props => props.theme.gray5};
				border-radius: ${rem('36px')};
				margin: 0 ${rem('8px')} 0 0;
				color: ${props => props.theme.gray7};
				font-weight: 400;
				font-size: ${rem('14px')};
				line-height: ${rem('16px')};
				letter-spacing: 1px;
				text-align: center;
				display: flex;
				justify-content: center;
				min-width: ${rem('40px')};
				max-width: ${rem('52px')};
				width: auto;

				@media ${props => props.theme.breakpointMedium} {
					font-size: ${rem('18px')};
					line-height: ${rem('18px')};
					border-radius: ${rem('36px')};
					border: 2px solid ${props => props.theme.gray7};
					margin: 0 ${rem('16px')} 0 0;
					padding: ${rem('7px')} ${rem('18px')};
					max-width: auto;
				}
			}
		}
	}
`;

TeamSideInfo.defaultProps = {
	nick: 'User',
	distance: 0,
	className: 'large-2 small-12',
	float: false,
};

TeamSideInfo.propTypes = {
	photoUrl: PropTypes.string,
	nick: PropTypes.string,
	distance: PropTypes.number,
	position: PropTypes.number,
	className: PropTypes.string,
	float: PropTypes.bool,
};

export default TeamSideInfo;



// WEBPACK FOOTER //
// ./src/components/general/modal/types/team-detail/team-side-info.jsx